import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';

export type ColumnFilter = ColumnFiltersState[number];

export interface TablePaginationState {
    pageIndex: number;
    pageSize: number;
}

export interface FilterCondition {
    condition: 'contains' | 'equals';
    value: string;
}

export interface DateFilterCondition {
    condition: 'is-equal-date' | 'is-between-date' | 'is-after-date' | 'is-before-date';
    value: string[];
}

export type QueryFilterValue = string | string[] | FilterCondition | DateFilterCondition | undefined;

export type QueryFilters = Record<string, QueryFilterValue>;

export interface BaseListOptions<TSortField extends string, TFilters extends QueryFilters> {
    accountId: string;
    search?: string;
    sortBy?: {
        field: TSortField;
        direction: 'asc' | 'desc';
    };
    pagination?: {
        page: number;
        pageSize: number;
    };
    filters?: TFilters;
}

export interface BaseListResult<_TData> {
    total: number;
    pageCount: number;
    options: Record<string, any>;
}

// Main table state interface for CRM app
export interface MainTableState {
    pagination: PaginationState;
    sorting: SortingState;
    columnFilters: ColumnFiltersState;
}

/**
 * Transforms table state from TanStack Table to the format expected by tRPC
 * Follows the exact same pattern as the main app's transformTableStateToQueryInput
 * but adds pagination conversion for tRPC (0-based to 1-based)
 */
export function transformTableStateToTRPCInput<TSortField extends string>(
    pagination: PaginationState,
    sorting: SortingState,
    columnFilters: ColumnFiltersState,
    filterMapping?: Record<string, string | ((value: any) => any)>,
    sortMapping?: Record<string, TSortField>,
) {
    // Transform filters based on mapping
    const transformedFilters: QueryFilters = {};

    columnFilters.forEach((filter) => {
        const { id, value } = filter;

        if (filterMapping && filterMapping[id]) {
            const mapping = filterMapping[id];
            if (typeof mapping === 'function') {
                // Custom transformation function
                const transformed = mapping(value);
                if (transformed !== undefined) {
                    Object.assign(transformedFilters, transformed);
                }
            } else {
                // Simple field name mapping
                transformedFilters[mapping] = value as QueryFilterValue;
            }
        } else {
            // Direct mapping (fallback)
            transformedFilters[id] = value as QueryFilterValue;
        }
    });

    return {
        pagination: {
            page: pagination.pageIndex + 1, // Convert 0-based to 1-based for tRPC
            pageSize: pagination.pageSize,
        },
        sortBy:
            sorting.length > 0
                ? {
                    field: (sortMapping && sortMapping[sorting[0].id]) || (sorting[0].id as TSortField),
                    direction: sorting[0].desc ? ('desc' as const) : ('asc' as const),
                }
                : undefined,
        filters: transformedFilters,
    };
}

// Helper functions for filter value transformation
export const getSingleValue = (value: any): string => {
    if (Array.isArray(value)) {
        return value[0] || '';
    }
    return String(value || '');
};

export const getArrayValue = (value: any): string[] => {
    if (Array.isArray(value)) {
        return value;
    }
    return value ? [String(value)] : [];
};

